import React, { useState, useEffect } from 'react';
import LampThumbnail from '../LampThumbnail';
import './OrderSummary.css';

// Helper function to validate S3 URLs (filter out base64)
const isValidS3Url = (url) => {
  return url && typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));
};

// Format price to always reserve space for 4 digits before decimal
function formatPrice(amount) {
  const formatted = amount.toFixed(2);
  const parts = formatted.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Return price with $ directly before number (no spaces)
  // CSS will handle the width reservation
  return `$${integerPart}.${decimalPart}`;
}

function OrderSummary({ items, subtotal, shippingCost, tax, total, onUpdateQuantity, onRemoveItem, onAddToCart }) {
  const [removedItem, setRemovedItem] = useState(null);
  const [undoTimeout, setUndoTimeout] = useState(null);
  const [itemThumbnails, setItemThumbnails] = useState({});

  // Clear undo when items change (user did another action)
  useEffect(() => {
    if (removedItem) {
      setRemovedItem(null);
      if (undoTimeout) {
        clearTimeout(undoTimeout);
        setUndoTimeout(null);
      }
    }
  }, [items.length]);

  // Fetch thumbnails for items that only have designId (from order data)
  useEffect(() => {
    const fetchThumbnails = async () => {
      const thumbnailPromises = items.map(async (item, index) => {
        const itemKey = item.id || item.designId || `item-${index}`;
        
        // If item already has a valid S3 thumbnail, use it
        if (item.thumbnail && isValidS3Url(item.thumbnail)) {
          setItemThumbnails(prev => ({ ...prev, [itemKey]: item.thumbnail }));
          return;
        }

        // If item has design with thumbnail, use it
        if (item.design?.thumbnail && isValidS3Url(item.design.thumbnail)) {
          setItemThumbnails(prev => ({ ...prev, [itemKey]: item.design.thumbnail }));
          return;
        }

        // If item has designId but no thumbnail, fetch the design
        if (item.designId && !item.thumbnail && !item.design?.thumbnail) {
          try {
            const token = localStorage.getItem('token');
            const response = await fetch(
              `${import.meta.env.VITE_API_URL}/api/lamp-designs/admin/${item.designId}`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );

            if (response.ok) {
              const design = await response.json();
              const thumbnail = design.thumbnail;
              
              // Only use S3 URLs, filter out base64
              if (isValidS3Url(thumbnail)) {
                setItemThumbnails(prev => ({ ...prev, [itemKey]: thumbnail }));
              } else if (thumbnail && thumbnail.startsWith('data:image')) {
                console.warn(`[OrderSummary] Design ${item.designId} has base64 thumbnail, ignoring`);
              }
            }
          } catch (error) {
            console.error(`[OrderSummary] Failed to fetch design ${item.designId} for thumbnail:`, error);
          }
        }
      });

      await Promise.all(thumbnailPromises);
    };

    fetchThumbnails();
  }, [items]);

  const handleRemoveItem = (itemId) => {
    if (!onRemoveItem) return;
    const item = items.find(i => i.id === itemId);
    if (item) {
      setRemovedItem(item);
      onRemoveItem(itemId);
      
      // Auto-hide undo after 5 seconds
      const timeout = setTimeout(() => {
        setRemovedItem(null);
      }, 5000);
      setUndoTimeout(timeout);
    }
  };

  const handleUndo = () => {
    if (!removedItem || !onUpdateQuantity) return;
    // Check if item still exists in cart
    const itemExists = items.find(i => i.id === removedItem.id);
    
    if (!itemExists && onAddToCart) {
      // Item doesn't exist, need to add it back
      // Create a copy of the item with quantity 1, add it, then update to original quantity
      const itemToAdd = { ...removedItem, quantity: 1 };
      onAddToCart(itemToAdd);
      // Update to original quantity after adding
      setTimeout(() => {
        if (onUpdateQuantity) {
          onUpdateQuantity(removedItem.id, removedItem.quantity);
        }
      }, 10);
    } else if (itemExists) {
      // Item exists, just restore quantity
      onUpdateQuantity(removedItem.id, removedItem.quantity);
    }
    
    setRemovedItem(null);
    if (undoTimeout) {
      clearTimeout(undoTimeout);
      setUndoTimeout(null);
    }
  };

  const handleUpdateQuantity = (itemId, newQuantity) => {
    if (!onUpdateQuantity) return;
    // Prevent reducing quantity below 1
    if (newQuantity < 1) {
      return;
    }
    // Clear undo if user modifies quantity
    if (removedItem) {
      setRemovedItem(null);
      if (undoTimeout) {
        clearTimeout(undoTimeout);
        setUndoTimeout(null);
      }
    }
    onUpdateQuantity(itemId, newQuantity);
  };
  return (
    <div className="order-summary">
      <h3>Order Summary</h3>
      
      {removedItem && onUpdateQuantity && (
        <div className="undo-notification">
          <span>{removedItem.name} removed</span>
          <button className="undo-btn" onClick={handleUndo}>
            Undo
          </button>
        </div>
      )}
      
      <div className="summary-items">
        {items.map((item) => (
          <div key={item.id} className="summary-item">
            <div className="summary-item-thumb">
              {(() => {
                // Get thumbnail from various sources, prioritizing S3 URLs
                const itemKey = item.id || item.designId || `item-${items.indexOf(item)}`;
                let thumbnail = item.thumbnail || item.design?.thumbnail || itemThumbnails[itemKey];
                
                // Only use S3 URLs
                if (thumbnail && !isValidS3Url(thumbnail)) {
                  thumbnail = null;
                }

                if (thumbnail) {
                  return (
                    <LampThumbnail 
                      design={item.design || (item.baseShape ? {
                        baseShape: item.baseShape,
                        modifiers: item.modifiers,
                        rendering: item.rendering,
                        noiseSeed: item.noiseSeed,
                        id: item.id
                      } : null)}
                      thumbnail={thumbnail}
                      alt={item.name} 
                    />
                  );
                } else if (item.design) {
                  return <LampThumbnail design={item.design} alt={item.name} />;
                } else if (item.baseShape && item.modifiers) {
                  return (
                    <LampThumbnail
                      design={{
                        baseShape: item.baseShape,
                        modifiers: item.modifiers,
                        rendering: item.rendering,
                        noiseSeed: item.noiseSeed,
                        id: item.id
                      }}
                      alt={item.name}
                    />
                  );
                }
                return null;
              })()}
            </div>
            <div className="summary-item-info">
              <div className="summary-item-name">{item.name}</div>
              {onUpdateQuantity && onRemoveItem && (
                <div className="summary-item-controls">
                  <div className="summary-item-qty">
                    <button 
                      className="qty-btn" 
                      onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      aria-label={`Decrease quantity of ${item.name}`}
                    >
                      -
                    </button>
                    <span className="qty-value">{item.quantity}</span>
                    <button 
                      className="qty-btn" 
                      onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                      aria-label={`Increase quantity of ${item.name}`}
                    >
                      +
                    </button>
                  </div>
                  <button 
                    className="remove-btn" 
                    onClick={() => handleRemoveItem(item.id)}
                    aria-label={`Remove ${item.name} from cart`}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              )}
              {!onUpdateQuantity && !onRemoveItem && (
                <div className="summary-item-qty-display">
                  <span>Qty: {item.quantity}</span>
                </div>
              )}
            </div>
            <div className="summary-item-price">
              {formatPrice(parseFloat(item.price.replace(/[^0-9.-]+/g, '')) * item.quantity)}
            </div>
          </div>
        ))}
      </div>

      <div className="summary-totals">
        <div className="summary-row">
          <span>Subtotal</span>
          <span>{formatPrice(subtotal)}</span>
        </div>
        <div className="summary-row">
          <span>Shipping</span>
          <span>{shippingCost === 0 ? 'Free' : formatPrice(shippingCost)}</span>
        </div>
        <div className="summary-row">
          <span>Tax</span>
          <span>{formatPrice(tax)}</span>
        </div>
        <div className="summary-row summary-total">
          <span>Total</span>
          <span>{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  );
}

export default OrderSummary;

